/**
 * Onde ficam os quadros baixados — disco ou RAM — e quanto espaço o loop tem.
 *
 * Na partida, antes do primeiro plano, mede o que o aparelho oferece:
 * - o espaço que o navegador concede ao site (`navigator.storage.estimate`);
 * - se o Cache Storage abre (janela anônima do Firefox, por exemplo, não).
 *
 * Com disco, o orçamento dos arquivos comprimidos passa a ser o do disco
 * (80% do concedido, até 12 GB) e a RAM guarda só os quadros decodificados.
 * Sem disco — ou com menos espaço que a própria RAM daria —, fica como era: os
 * arquivos na RAM, com o orçamento do perfil do aparelho (budget.ts).
 *
 * A faxina do disco também mora aqui (`sweepDisk`): o que o plano não quer
 * mais e não vale guardar (ver `retainOnDisk`) é apagado.
 */
import { create } from 'zustand';
import type { DeviceProfile } from './budget';
import {
  diskBudgetBytes,
  estimateQuota,
  openDiskStore,
  retainOnDisk,
  type BlobStore,
  type DiskRetention,
} from './diskStore';
import type { FrameCache } from './frameCache';

export type StorageMode = 'pendente' | 'disco' | 'memoria';

export interface FrameStorageState {
  /** `pendente` enquanto mede o aparelho (o controlador espera). */
  mode: StorageMode;
  /** Orçamento dos arquivos comprimidos no disco (null = na RAM, orçamento do perfil). */
  diskBudget: number | null;
}

/** Fora do app (testes, verificação) os arquivos ficam na RAM, como antes. */
export const useFrameStorage = create<FrameStorageState>()(() => ({ mode: 'memoria', diskBudget: null }));

/** Tempo máximo esperando a medição antes de seguir com a RAM. */
const STARTUP_TIMEOUT_MS = 1500;

/** Orçamento dos arquivos comprimidos: o do disco, se houver; senão o da RAM. */
export function compressedBudget(profile: DeviceProfile, state: FrameStorageState = useFrameStorage.getState()): number {
  return state.diskBudget ?? profile.budgetBytes;
}

/**
 * Disco só vale se der MAIS espaço que a RAM já dá: numa janela anônima o
 * Chrome concede pouco, e aí o Cache Storage fica na memória do mesmo jeito.
 */
export function chooseStorage(hasStore: boolean, quota: number | undefined, ramBudget: number): number | null {
  if (!hasStore) return null;
  const disk = diskBudgetBytes(quota);
  return disk !== null && disk > ramBudget ? disk : null;
}

let store: BlobStore | null = null;

/**
 * Mede o aparelho e liga o depósito em disco no `cache`. O estado vai a
 * `pendente` na hora e termina em `disco` ou `memoria`.
 */
export function startFrameStorage(cache: FrameCache, profile: DeviceProfile): () => void {
  let stopped = false;
  useFrameStorage.setState({ mode: 'pendente', diskBudget: null });

  const measure = async (): Promise<void> => {
    const [opened, quota] = await Promise.all([openDiskStore(), estimateQuota()]);
    if (stopped || useFrameStorage.getState().mode !== 'pendente') return;
    const budget = chooseStorage(opened !== null, quota, profile.budgetBytes);
    if (budget === null || !opened) {
      useFrameStorage.setState({ mode: 'memoria', diskBudget: null });
      return;
    }
    store = opened;
    cache.setStore(opened, (e) => onFull(cache, e));
    useFrameStorage.setState({ mode: 'disco', diskBudget: budget });
  };

  void measure().catch(() => useFrameStorage.setState({ mode: 'memoria', diskBudget: null }));
  // Medição travada não segura o portal: segue com a RAM.
  const timer = setTimeout(() => {
    if (useFrameStorage.getState().mode === 'pendente') useFrameStorage.setState({ mode: 'memoria', diskBudget: null });
  }, STARTUP_TIMEOUT_MS);

  return () => {
    stopped = true;
    clearTimeout(timer);
    cache.setStore(null);
    store = null;
  };
}

/**
 * O disco recusou um arquivo: o orçamento encolhe para o que já está guardado
 * (com 10% de folga), e o controlador recorta o loop nos quadros mais recentes.
 */
function onFull(cache: FrameCache, e: unknown): void {
  if (!(e instanceof DOMException) || e.name !== 'QuotaExceededError') return;
  const { diskBudget } = useFrameStorage.getState();
  const stored = cache.stats().bytes - cache.stats().memoryBytes;
  const next = Math.floor(stored * 0.9);
  if (diskBudget !== null && next < diskBudget) useFrameStorage.setState({ diskBudget: Math.max(next, 1) });
}

/**
 * Apaga do disco o que não está no cache e não vale guardar. Devolve quantos
 * arquivos saíram.
 */
export async function sweepDisk(cache: FrameCache, retention: DiskRetention, target: BlobStore | null = store): Promise<number> {
  if (!target) return 0;
  let removed = 0;
  for (const url of await target.keys()) {
    if (cache.has(url) || retainOnDisk(url, retention)) continue;
    await target.delete(url).catch(() => {});
    removed++;
  }
  return removed;
}
